/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { convertToString } from 'app/shared/lib/utilities';
import { FormatterRegistry } from 'app/shared/lib/formatting/formatter-registry';
import { IDateTimeFormatOptions, IRelativeDateTimeFormatOptions, INumberFormatOptions, IParseOptions, INumberParseOptions } from 'app/shared/lib/formatting/formatting-types';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { getLanguageLocale } from 'app/shared/lib/formatting/formatting-utilities';

export type DateTimeTypes = string | DateTimeValue | DateValue | Date | null | undefined;
export type NumberTypes = string | number | null | undefined;

const nonNumericCharactersRegex = /[^\d\+\-\.]/g;

/**
 * Provides functions for formatting values of various types.
 */
@autoinject
export class FormatProvider {

    protected readonly numberLocaleKey: string = 'ui:locales.number';

    constructor(public readonly registry: FormatterRegistry, private readonly env: EnvironmentService, private readonly i18n: I18N) {
        env.registerDebugObject('formatProvider', this);
    }

    /**
    * Format the specified `DateTimeValue`, `DateValue`, `Date` or `string` value as a timestamp,
    * using appropriate culture localization settings.
    *
    * When specifying a value for `format`, use one of the following patterns:
    * * `undefined` or `''` to look up the format resource specified by `ui:formats.timestamp.default`.
    * * `'default'` or `'long'` to look up the format resource specified by `ui:formats.timestamp.default` or `ui:formats.timestamp.long`.
    * * `'ns:formats.customTimestamp.default'` to look up the format resource specified by `ns:formats.customTimestamp.default`.
    * * `'!M/d'` to use the explicit format `'M/d'`.
    *
    * @param value a `DateTimeValue`, a `Date`, or a `string` in the ISO 8601 Date format (`yyyy-MM-ddTHH:mm:ss.SSSZ`)
    * @param options (*optional*) options to use when formatting the value
    */
    public formatTimestamp(value: DateTimeTypes, options: IDateTimeFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'timestamp', options), true);
    }

    /**
    * Format the specified `DateTimeValue`, `DateValue`, `Date` or `string` value as a date/time,
    * using appropriate culture localization settings.
    *
    * When specifying a value for `format`, use one of the following patterns:
    * * `undefined` or `''` to look up the format resource specified by `ui:formats.dateTime.default`.
    * * `'default'` or `'long'` to look up the format resource specified by `ui:formats.dateTime.default` or `ui:formats.dateTime.long`.
    * * `'ns:formats.customDateTime.default'` to look up the format resource specified by `ns:formats.customDateTime.default`.
    * * `'!M/d'` to use the explicit format `'M/d'`.
    *
    * @param value a `DateTimeValue`, a `Date`, or a `string` in the ISO 8601 Date format (`yyyy-MM-ddTHH:mm:ssZ`)
    * @param options (*optional*) options to use when formatting the value
    */
    public formatDateTime(value: DateTimeTypes, options: IDateTimeFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'dateTime', options), true);
    }

    /**
     * Format the specified `DateTimeValue`, `DateValue`, `Date` or `string` as a time,
     * using appropriate culture localization settings.
     *
     * When specifying a value for `format`, use one of the following patterns:
     * * `undefined` or `''` to look up the format resource specified by `ui:formats.time.default`.
     * * `'default'` or `'long'` to look up the format resource specified by `ui:formats.time.default` or `ui:formats.time.long`.
     * * `'ns:formats.customDate.default'` to look up the format resource specified by `ns:formats.customDate.default`.
     * * `'!HH:mm'` to use the explicit format `'HH:mm'`.
     *
     * @param value a `DateTimeValue`, a `Date`, or a `string` in the ISO 8601 Date format (`yyyy-MM-ddTHH:mm:ssZ`)
     * @param options (*optional*) options to use when formatting the value
     */
    public formatTime(value: DateTimeTypes, options: IDateTimeFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'time', options), true);
    }

    /**
     * Format the specified `DateTimeValue`, `DateValue`, `Date` or `string` as a date,
     * using appropriate culture localization settings.
     *
     * When specifying a value for `format`, use one of the following patterns:
     * * `undefined` or `''` to look up the format resource specified by `ui:formats.date.default`.
     * * `'default'` or `'long'` to look up the format resource specified by `ui:formats.date.default` or `ui:formats.date.long`.
     * * `'ns:formats.customDate.default'` to look up the format resource specified by `ns:formats.customDate.default`.
     * * `'!M/d'` to use the explicit format `'M/d'`.
     *
     * @param value a `DateValue`, a `Date` or a `string` in the ISO 8601 Date format (`yyyy-MM-dd`)
     * @param options (*optional*) options to use when formatting the value
     */
    public formatDate(value: DateTimeTypes, options: IDateTimeFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'date', options), true);
    }

    /**
     * **Documentation To Be Provided**
     *
     * @param value
     * @param options
     */
    public formatNumber(value: NumberTypes, options: INumberFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'number', options), true);
    }

    /**
     * **Documentation To Be Provided**
     *
     * @param value
     * @param options
     */
    public formatDecimal(value: NumberTypes, options: INumberFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'decimal', options), true);
    }

    /**
     * **Documentation To Be Provided**
     *
     * @param value
     * @param options
     */
    public formatInteger(value: NumberTypes, options: INumberFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'integer', options), true);
    }

    /**
     * **Documentation To Be Provided**
     *
     * @param value A decimal value between 0 and 1
     * @param options
     */
    public formatPercent(value: NumberTypes, options: INumberFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'percent', options), true);
    }

    /**
     * **Documentation To Be Provided**
     *
     * @param value
     * @param options
     */
    public formatCurrency(value: NumberTypes, options: INumberFormatOptions = {}): string | null {
        return convertToString(this.registry.formatDirect(value, 'currency', options), true);
    }

    public formatRelativeDateTime(value: string | null, options: IRelativeDateTimeFormatOptions = {}): string | null {
        if(!value) { return null; }

        const now = new DateTimeValue(this.env.now());
        const other = new DateTimeValue(value);
        const difference = now.difference(other, 'seconds');
        const seconds = Math.abs(difference);
        let count = 0;

        let key: string = 'zero';
        if(options.seconds !== false && seconds < 60) { key = 'second'; count = seconds; }
        else if(options.minutes !== false && seconds < 60 * 60) { key = 'minute'; count = seconds / 60; }
        else if(options.hours !== false && seconds < 60 * 60 * 24) { key = 'hour'; count = seconds / 60 / 60; }
        else if(options.days !== false && seconds < 60 * 60 * 24 * 7) { key = 'day'; count = seconds / 60 / 60 / 24; }
        else if(options.weeks !== false) { key = 'week'; count = seconds / 60 / 60 / 24 / 7; }

        if(!key) { return null; }

        const sign = difference < 0 ? 'future' : 'past';
        const formatOptions = { count: Math.round(count), date: value };
        return this.i18n.tr(`ui:relativeDateTime.${sign}.${key}`, formatOptions);
    }

    /**
     * **Work in progress, but adding this for consistency**
     *
     * @param value
     */
    public parseNumber(value: string | null, options: INumberParseOptions = {}): number | null {
        if(!value) { return null; }

        const localeCode = getLanguageLocale(this.i18n, this.numberLocaleKey, options);

        // From: https://stackoverflow.com/questions/29255843/is-there-a-way-to-reverse-the-formatting-by-intl-numberformat-in-javascript
        const thousandSeparator = (1111).toLocaleString(localeCode).replace(/1/g, '');
        const decimalSeparator = (1.1).toLocaleString(localeCode).replace(/1/g, '');

        const convertedValue = value
            .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
            .replace(new RegExp('\\' + decimalSeparator), '.')
            .replace(nonNumericCharactersRegex, '');

        const result = parseFloat(convertedValue);

        if(Number.isNaN(result)) { return null; }
        return result;
    }

    protected prepareForParse(value: string | null, options: IParseOptions): string | null {
        if(!value) { return null; }

        if(options.trim !== false) {
            return value.trim();
        }

        return value;
    }

}

